import { useRef, useCallback, MouseEvent, useEffect } from "react";

export function useDraggableScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = useCallback((e: MouseEvent<T>) => {
    if (!ref.current) return;
    isDragging.current = true;
    ref.current.style.cursor = "grabbing";
    ref.current.style.userSelect = "none";
    ref.current.style.setProperty("-webkit-user-select", "none");
    startX.current = e.pageX - ref.current.offsetLeft;
    scrollLeft.current = ref.current.scrollLeft;
  }, []);

  const onDragEnd = useCallback(() => {
    isDragging.current = false;
    if (ref.current) {
      ref.current.style.cursor = "pointer";
      ref.current.style.userSelect = "auto";
      ref.current.style.setProperty("-webkit-user-select", "auto");
    }
  }, []);

  const onMouseMove = useCallback((e: MouseEvent<T>) => {
    if (!isDragging.current || !ref.current) return;
    e.preventDefault();
    const x = e.pageX - ref.current.offsetLeft;
    const walk = (x - startX.current) * 2; // Scroll speed multiplier
    ref.current.scrollLeft = scrollLeft.current - walk;
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaX === 0 && e.deltaY === 0) return;

      // Tomamos el movimiento dominante (horizontal o vertical)
      const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const delta = isHorizontal ? e.deltaX : e.deltaY;

      const isAtLeftEdge = el.scrollLeft <= 0 && delta < 0;
      const isAtRightEdge =
        Math.ceil(el.scrollLeft) >= el.scrollWidth - el.clientWidth && delta > 0;

      // Prevenimos el scroll vertical de la página y lo convertimos en horizontal
      // a menos que hayamos llegado a los topes
      if (!isAtLeftEdge && !isAtRightEdge) {
        e.preventDefault();
        el.scrollLeft += delta;
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  return {
    ref,
    onMouseDown,
    onMouseLeave: onDragEnd,
    onMouseUp: onDragEnd,
    onMouseMove,
  };
}
